import CONSTANTS from '@/shared/constants/constants'
import DicomPlusMixin from '@/shared/mixins/dicom-plus.mixin'
import XPlusMixin from '@/shared/mixins/x-plus.mixin'
import { showDialog } from '@/shared/sweetAlerts/showDialog'
import { removeValoresVazios } from '@/shared/utils/clean-empty-objects'
import { extrairMensagemErroApi, isErroPedidoMedico } from '@/shared/utils/api-error.helper'
import { aplicarMascaraCpf, validarCpf } from '@/shared/utils/cpf-validator'
import {
  aplicarMascaraDeData,
  aplicarMascaraEValidarData,
  dateBrToInt,
  formatDateBR,
  isDataValida
} from '@/shared/utils/date-validator'
import Swal from 'sweetalert2'
import DicomPlusGestorSaude from '../../dicom-plus/dicom-plus-gestor-saude/index.vue'
import DicomPlusSoc from '../../dicom-plus/dicom-plus-soc/index.vue'
import AnexarArquivosModal from './x-plus-import-files-modal.vue'
import XPlusModalFooter from './x-plus-modal-footer.vue'
import XPlusModalTabComplementares from './x-plus-modal-tab-complementares/index.vue'
import PatientSocialNameInfoIcon from '@/components/patient-social-name-info-icon/index.vue'

export default {
  name: 'x-plus-modal',
  props: ['exam'],
  mixins: [XPlusMixin, DicomPlusMixin],
  components: {
    AnexarArquivosModal,
    XPlusModalFooter,
    XPlusModalTabComplementares,
    DicomPlusSoc,
    DicomPlusGestorSaude,
    PatientSocialNameInfoIcon
  },
  data() {
    return {
      activeTab: 'tracado',
      paciente: {
        nome: '',
        dataNascimento: '',
        sexo: '',
        socialName: '',
        patientID: '',
        dicomPlusID: '',
        exameID: ''
      },
      tracado: {
        unidade: '',
        especialidade: '',
        comentarios: '',
        prioridade: null,
        especialidadeOptions: [],
        prioridadeOptions: []
      },
      camposComplementares: [],
      formComplementares: {},
      showAnexarArquivos: false,
      anexoTipoOptions: [],
      savedFiles: [],
      attachedFilesCount: 0,
      cpfValido: true,
      dataNascimentoValida: true,
      especialidadeSearch: '',
      dropdownEspecialidadeAberto: false,
      especialidadeFiltrada: [],
      isExamNotExistGestorSaude: false,
      requisicaoID: '',
      allFilesSoc: false,
      mostrarSoc: false,
      campoSocAtivo: null,
      socItems: [],
      socTotal: 0,
      socPageCount: 1,
      socPage: 1,
      socPageSize: 10,
      selectedFichaId: null,
      mostrarGSaude: false,
      objetoPaciente: null,
      dirtyBit: null,
      isRequiredPatientData: true,
      exameCriado: false
    }
  },
  watch: {
    exam: {
      immediate: true,
      handler(newExam) {
        this.paciente.nome = newExam?.paciente || ''
        this.paciente.dataNascimento = newExam?.dataNascimento || ''
        this.paciente.sexo = newExam?.sexo || ''
        this.paciente.socialName = newExam?.socialName || ''
        this.paciente.patientID = newExam?.patientID || ''
      }
    },
    'tracado.especialidade': {
      handler(nv) {
        const list = this.tracado.especialidadeOptions
        if (Array.isArray(list) && list.length) {
          const sel = list.find(e => String(e.value) === String(nv))
          if (sel) this.especialidadeSearch = sel.label
        }
      }
    }
  },
  methods: {
    async carregarEspecialidades() {
      const especialidades = await this.getSpecialityListXPlus(this.exameCriado)
      const lista = (especialidades || []).map(e => ({ value: e.value, label: e.label, requiredPatientData: e.requiredPatientData ?? true }))
      this.tracado.especialidadeOptions = lista

      if (this.tracado.especialidade) {
        const sel = lista.find(e => String(e.value) === String(this.tracado.especialidade))
        this.especialidadeSearch = sel ? sel.label : ''
        this.isRequiredPatientData = sel ? sel.requiredPatientData ?? true : true
      }
    },

    // Mesma regra do Dicom Plus: Emergencia so aparece para TC com fonte que permite.
    buscarPrioridades() {
      let prioridadesOrdenados = this.getPriority().sort((a, b) => a.label.localeCompare(b.label))
      if (this.exam?.fonteEmergenciaOpcional !== true || this.exam?.categoria !== CONSTANTS.DICOM_PLUS_MODALIDADE.TC) {
        prioridadesOrdenados = prioridadesOrdenados.filter(
          p => p.label.toLowerCase() !== 'emergência' && p.label.toLowerCase() !== 'emergencia'
        )
      }
      this.tracado.prioridadeOptions = prioridadesOrdenados
    },

    async buscarCamposComplementares() {
      const fonteID = this.exam.fonteID
      let campos = await this.getDadosComplementares(fonteID)
      campos = campos.sort((a, b) => a.posicao - b.posicao)
      this.camposComplementares = campos

      let dadosSalvos = this.exam.dadosComplementares || this.exam.tagsPlus || {}
      if (typeof dadosSalvos === 'string') {
        try { dadosSalvos = JSON.parse(dadosSalvos) } catch (e) { dadosSalvos = {} }
      }
      campos.forEach(campo => {
        let valor = dadosSalvos[campo.titulo] ?? ''
        if ((campo.special === 1 || campo.special === 2) && typeof valor === 'number') {
          valor = String(valor)
        }
        this.$set(this.formComplementares, campo.titulo, valor)
      })
    },

    async buscarPaciente() {
      const dicomPlusID = this.exam.dicomPlusID
      this.exameCriado = this.exam.exameCriado
      if (!dicomPlusID) return
      try {
        this.$root.$emit('showLoading')
        const result = await this.getDadosPorPacienteXPlus(dicomPlusID)
        const dados = Array.isArray(result) && result.length > 0 ? result[0] : {}
        Object.assign(this.tracado, {
          unidade: dados?.unidade || '',
          especialidade: dados?.especialidade || '',
          comentarios: dados?.comentarios || '',
          prioridade: dados?.prioridade || this.exam?.prioridade || CONSTANTS.DICOM_PLUS_PRIORIDADE.ROTINA
        })
        Object.assign(this.paciente, {
          nome: dados?.paciente || '',
          dataNascimento: dados?.dataNascimento || '',
          sexo: dados?.sexo || '',
          socialName: dados?.socialName || '',
          patientID: dados?.patientID || '',
          dicomPlusID: dados?.dicomPlusID || '',
          exameID: dados?.exameID || ''
        })
        this.isExamNotExistGestorSaude = dados?.isExamNotExistGestorSaude || false
        this.requisicaoID = dados?.requisicaoID || ''
      } catch (error) {
        showDialog({
          icon: 'error',
          title: this.$t('x_plus.dialog_message.search_data_error'),
          text: error,
          confirmButtonText: 'OK'
        })
      } finally {
        this.$root.$emit('hideLoading')
      }
    },

    storeInitialState() {
      this.dirtyBit = JSON.stringify({
        paciente: { ...this.paciente },
        tracado: { ...this.tracado },
        formComplementares: { ...this.formComplementares }
      })
    },

    isDirty() {
      const current = JSON.stringify({
        paciente: { ...this.paciente },
        tracado: { ...this.tracado },
        formComplementares: { ...this.formComplementares }
      })
      return current !== this.dirtyBit
    },

    async confirmarSair() {
      if (this.isDirty() && this.exam?.status !== CONSTANTS.DICOM_PLUS_STATUS.REVISADO) {
        await showDialog({
          title: this.$t('x_plus.dialog_message.confirmation'),
          text: this.$t('x_plus.dialog_message.confirm_exit'),
          confirmButtonText: this.$t('x_plus.yes'),
          denyButtonText: this.$t('x_plus.no'),
          icon: 'question',
          focusConfirm: false,
          focusDeny: true,
          confirmed: this.fechar
        })
      } else {
        await this.fechar()
      }
    },

    async fechar() {
      if (this.exam?.status === CONSTANTS.DICOM_PLUS_STATUS.PENDENTE) {
        this.$root.$emit('showLoading')
        await this.removeAssignedUser(this.exam.dicomPlusID)
        this.$root.$emit('hideLoading')
        this.$emit('refreshGrid')
      }
      this.$emit('close')
    },

    async confirmarSalvar() {
      this.validarEspecialidadeInput()
      const { nome, dataNascimento, sexo } = this.paciente

      let obrigatorios

      if (this.isRequiredPatientData)
      {
        obrigatorios = [
          { nome: 'Especialidade', valor: this.tracado?.especialidade },
          { nome: 'Nome do paciente', valor: nome?.trim() },
          { nome: 'Data de nascimento', valor: dataNascimento },
          { nome: 'Sexo', valor: sexo?.trim() }
        ]
      }
      else
      {
        obrigatorios = [
          { nome: 'Especialidade', valor: this.tracado?.especialidade },
        ]
      }

      const faltando = obrigatorios.filter(c => !c.valor)
      if (faltando.length) {
        showDialog({
          icon: 'warning',
          title: this.$t('x_plus.dialog_message.required_fields'),
          text: faltando.map(c => c.nome).join(', '),
          confirmButtonText: 'OK'
        })
        return
      }
      if (!isDataValida(dataNascimento)) {
        showDialog({
          icon: 'warning',
          title: this.$t('x_plus.dialog_message.invalid_data'),
          text: this.$t('x_plus.dialog_message.invalid_date_of_birth'),
          confirmButtonText: 'OK'
        })
        return
      }
      await showDialog({
        title: this.$t('x_plus.dialog_message.confirmation'),
        text: this.$t('x_plus.dialog_message.confirm_save'),
        confirmButtonText: this.$t('x_plus.yes'),
        denyButtonText: this.$t('x_plus.no'),
        icon: 'question',
        focusConfirm: false,
        focusDeny: true,
        confirmed: this.salvarDados
      })
    },

    async salvarDados() {
      const { nome, dataNascimento, sexo, patientID, socialName } = this.paciente
      const dadosComplementaresJSON = {}
      this.camposComplementares.forEach(campo => {
        dadosComplementaresJSON[campo.titulo] = this.formComplementares[campo.titulo]
      })
      const tagsPlusLimpo = removeValoresVazios(dadosComplementaresJSON, ['requisicaoID'])
      const novoStatus = this.exam.status === CONSTANTS.DICOM_PLUS_STATUS.PENDENTE
        ? CONSTANTS.DICOM_PLUS_STATUS.REVISADO
        : this.exam.status

      const payload = {
        dicomPlusID: this.exam.dicomPlusID,
        studyInstanceUID: this.exam.studyInstanceUID,
        accessionNumber: this.exam.accessionNumber || null,
        stationName: this.exam.stationName || null,
        fonteID: this.exam.fonteID,
        modality: this.exam.categoria || null,
        patientName: nome.trim(),
        patientID: patientID || null,
        patientBirthDate: dateBrToInt(dataNascimento),
        patientSex: sexo.trim(),
        patientSocialName: socialName && socialName.trim() ? socialName.trim() : null,
        studyDescription: this.exam.studyDescription || null,
        patientComments: this.tracado.comentarios || null,
        tituloLaudo: this.exam.tituloLaudo || null,
        status: novoStatus,
        studyDate: this.exam.studyDate,
        studyTime: this.exam.studyTime,
        requestingPhysicianName: this.exam.requestingPhysicianName || null,
        patientWeight: this.exam.patientWeight || null,
        token: this.exam.token || null,
        especialidadeID: this.tracado.especialidade,
        subEspecialidade: this.exam.subEspecialidade || null,
        contraste: this.exam.contraste || null,
        contrasteVia: this.exam.contrasteVia || null,
        contrasteTipo: this.exam.contrasteTipo || null,
        trauma: this.exam.trauma || null,
        avc: this.exam.avc || null,
        leito: this.exam.leito || null,
        oncologico: this.exam.oncologico || null,
        prioridade: this.tracado.prioridade || this.exam.prioridade || CONSTANTS.DICOM_PLUS_PRIORIDADE.ROTINA,
        enviaParaGSaudeApp: this.exam.enviaParaGSaudeApp,
        requisicaoID: Number(this.requisicaoID) || null,
        isExamNotExistGestorSaude: !!this.isExamNotExistGestorSaude,
        tagsPlus: Object.keys(tagsPlusLimpo).length === 0 ? null : JSON.stringify(tagsPlusLimpo)
      }

      if (this.selectedFichaId) {
        payload.portalSocId = this.selectedFichaId
      }

      this.$root.$emit('showLoading')
      try {
        await this.updatePacienteXPlus(payload)
        this.$root.$emit('hideLoading')
        showDialog({
          icon: 'success',
          title: this.$t('x_plus.dialog_message.success'),
          text: this.$t('x_plus.dialog_message.registration_updated_succesfully'),
          confirmButtonText: 'OK',
          confirmed: () => {
            this.storeInitialState()
            this.$emit('refreshGrid')
            this.$emit('close')
          }
        })
      } catch (error) {
        this.$root.$emit('hideLoading')
        const mensagemErro = extrairMensagemErroApi(error, this.$t('error.unexpected_error_occurred'))
        const isPedidoMedicoPendente = isErroPedidoMedico(mensagemErro)
        showDialog({
          icon: isPedidoMedicoPendente ? 'warning' : 'error',
          title: isPedidoMedicoPendente
            ? this.$t('medical_order_required_title')
            : this.$t('x_plus.dialog_message.error_validating_data'),
          text: mensagemErro,
          confirmButtonText: 'OK'
        })
      }
    },

    async onDataNascimentoInput(data) {
      const { masked, isValid } = aplicarMascaraEValidarData(data.target.value)
      this.paciente.dataNascimento = masked
      this.dataNascimentoValida = isValid
      if (isValid) {
        const [day, month, year] = masked.split('/')
        const dateStr = `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`
        const result = await this.getPatientAge(dateStr)
        this.exam.idade = result.data || ''
      }
    },

    onCampoInput(titulo, event, campo) {
      let valor = event.target.value
      if (campo.special === CONSTANTS.DICOM_PLUS_FONTE_CABECALHO_TAG.CPF) {
        valor = aplicarMascaraCpf(String(valor))
        this.cpfValido = validarCpf(valor)
        event.target.value = valor
      }
      if (campo.special === CONSTANTS.DICOM_PLUS_FONTE_CABECALHO_TAG.DATA_LAUDO) {
        valor = aplicarMascaraDeData(String(valor))
        this.dataNascimentoValida = isDataValida(valor)
        event.target.value = valor
      }
      this.$set(this.formComplementares, titulo, valor)
    },

    async abrirSoc(campo) {
      this.$root.$emit('showLoading')
      try {
        this.campoSocAtivo = campo
        const cpf = campo
        const fonteID = this.exam?.fonteID
        const patientName = this.paciente.nome
        const { items, total, pageCount } = await this.getSocFiles({
          cpf: cpf.replace(/\D/g, ''),
          page: this.socPage,
          pageSize: this.socPageSize,
          fonteID,
          patientName,
          allFiles: this.allFilesSoc
        })
        this.socItems = items
        this.socTotal = total
        this.socPageCount = pageCount
        this.mostrarSoc = true
      } catch (e) {
        showDialog({
          icon: 'warning',
          title: this.$t('x_plus.no_data_found'),
          text: e.response?.data?.errors || this.$t('dicom_plus.soc.no_files_for_CPF'),
          confirmButtonText: 'OK'
        })
      } finally {
        this.$root.$emit('hideLoading')
      }
    },

    async onSocPageChange(newPage, newPageSize) {
      this.socPage = newPage
      this.socPageSize = newPageSize
      await this.abrirSoc(this.campoSocAtivo)
    },

    async onSelecionarFichaConfirmation(id, especialidadeCodigo, especialidadeNome) {
      await showDialog({
        title: this.$t('x_plus.dialog_message.confirmation'),
        text: this.$t('dicom_plus.soc.confirm_select_soc_file'),
        denyButtonText: this.$t('x_plus.no'),
        confirmButtonText: this.$t('dicom_plus.soc.select'),
        icon: 'question',
        focusConfirm: false,
        focusDeny: true,
        confirmed: () => this.onSelecionarFicha(id, especialidadeCodigo, especialidadeNome)
      })
    },

    onSelecionarFicha(portalSocId, especialidadeCodigo, especialidadeNome) {
      this.selectedFichaId = portalSocId
      this.mostrarSoc = false
      if (especialidadeCodigo && especialidadeNome) {
        this.especialidadeSearch = especialidadeNome
        this.tracado.especialidade = especialidadeCodigo
      }
    },

    async onSelecionarFichasConfirmation(selectedItems) {
      if (!selectedItems || !Array.isArray(selectedItems) || selectedItems.length === 0) return
      await showDialog({
        title: this.$t('x_plus.dialog_message.confirmation'),
        text: this.$t('dicom_plus.soc.confirm_select_soc_file'),
        denyButtonText: this.$t('x_plus.no'),
        confirmButtonText: this.$t('dicom_plus.soc.select'),
        icon: 'question',
        focusConfirm: false,
        focusDeny: true,
        confirmed: () => this.onSelecionarFichas(selectedItems)
      })
    },

    onSelecionarFichas(items) {
      try {
        if (!items || !items.length) return
        const dadosSoc = items[0]
        this.selectedFichaId = dadosSoc.portalSocId
        this.mostrarSoc = false

        if (dadosSoc.especialidadeCodigo && dadosSoc.especialidadeNome) {
          this.especialidadeSearch = dadosSoc.especialidadeNome
          this.tracado.especialidade = dadosSoc.especialidadeCodigo
        }
        if (dadosSoc.name !== undefined && dadosSoc.name !== null && String(dadosSoc.name).trim() !== '') {
          this.paciente.nome = dadosSoc.name
        }
        if (dadosSoc.cpf !== undefined && dadosSoc.cpf !== null && String(dadosSoc.cpf).trim() !== '' && validarCpf(dadosSoc.cpf)) {
          this.$set(this.formComplementares, this.cpfCampo?.titulo || 'CPF', aplicarMascaraCpf(dadosSoc.cpf))
          this.cpfValido = true
        }
        if (dadosSoc.birthDate !== undefined && dadosSoc.birthDate !== null && String(dadosSoc.birthDate).trim() !== '') {
          const dataBR = formatDateBR(dadosSoc.birthDate)
          if (isDataValida(dataBR)) {
            this.paciente.dataNascimento = dataBR
          }
        }
        if (dadosSoc.gender !== undefined && dadosSoc.gender !== null && String(dadosSoc.gender).trim() !== '') {
          const genero = String(dadosSoc.gender).trim()
          this.paciente.sexo = (genero === '1') ? 'M' : 'F'
        }
      } catch (e) {
        showDialog({
          icon: 'error',
          title: this.$t('x_plus.dialog_message.error'),
          text: this.$t('dicom_plus.soc.error_processing_multiple_selection'),
          confirmButtonText: 'OK'
        })
      }
    },

    async removerFichaConfirmacao() {
      await showDialog({
        title: this.$t('x_plus.dialog_message.confirmation'),
        text: this.$t('dicom_plus.soc.confirm_remove_soc_file'),
        denyButtonText: this.$t('x_plus.no'),
        confirmButtonText: this.$t('dicom_plus.soc.remove'),
        icon: 'question',
        focusConfirm: false,
        focusDeny: true,
        confirmed: this.removerFicha
      })
    },

    removerFicha() {
      this.selectedFichaId = null
    },

    async buscarDadosGestorSaude(solicitacaoId) {
      this.$root.$emit('showLoading')
      try {
        const pacienteCpf = this.formComplementares[this.cpfCampo?.titulo]
        const pacienteDataNascimento = this.exam.dataNascimento
        const modalityID = this.exam?.categoria
        const fonteID = this.exam?.fonteID
        const dados = await this.buscarDadosPacienteGestorSaude({
          solicitacaoId,
          modalityID,
          fonteID,
          pacienteCpf,
          pacienteDataNascimento
        })
        this.objetoPaciente = dados
        this.mostrarGSaude = true
      } catch (e) {
        showDialog({ icon: 'warning', title: this.$t('x_plus.no_data_found'), text: e, confirmButtonText: 'OK' })
        this.mostrarGSaude = false
      } finally {
        this.$root.$emit('hideLoading')
      }
    },

    async confirmaAtualizarDados(payload) {
      await showDialog({
        title: this.$t('x_plus.dialog_message.confirmation'),
        text: this.$t('dicom_plus.gestor_saude.confirm_replace'),
        denyButtonText: this.$t('x_plus.no'),
        confirmButtonText: this.$t('dicom_plus.gestor_saude.replace'),
        icon: 'question',
        focusConfirm: false,
        focusDeny: true,
        confirmed: () => this.atualizarDadosGestorSaude(payload)
      })
    },

    atualizarDadosGestorSaude(payload) {
      try {
        const especialidades = Array.isArray(payload.especialidades) ? payload.especialidades : []
        if (especialidades.length) {
          const primeira = especialidades[0]
          if (primeira.requisicaoId !== undefined && primeira.requisicaoId !== null) {
            this.requisicaoID = String(primeira.requisicaoId)
          }
          if (primeira.especialidadeID) {
            const match = this.tracado.especialidadeOptions.find(opt => String(opt.value) === String(primeira.especialidadeID))
            if (match) {
              this.tracado.especialidade = match.value
              this.especialidadeSearch = match.label
            }
          }
        }

        if (payload.nome !== undefined && payload.nome !== null && String(payload.nome).trim() !== '') {
          this.paciente.nome = payload.nome
        }
        if (payload.cpf !== undefined && payload.cpf !== null && String(payload.cpf).trim() !== '' && validarCpf(payload.cpf)) {
          this.$set(this.formComplementares, this.cpfCampo?.titulo || 'CPF', aplicarMascaraCpf(payload.cpf))
          this.cpfValido = true
        }
        if (payload.dataNascimento !== undefined && payload.dataNascimento !== null && String(payload.dataNascimento).trim() !== '') {
          const dataBR = formatDateBR(payload.dataNascimento)
          if (isDataValida(dataBR)) {
            this.paciente.dataNascimento = dataBR
          }
        }
        if (payload.comentarios !== undefined && payload.comentarios !== null && String(payload.comentarios).trim() !== '') {
          this.tracado.comentarios = payload.comentarios
        }

        this.mostrarGSaude = false
      } catch (e) {
        console.error('Erro ao confirmar atualização de dados Gestor Saúde:', e)
      }
    },

    widthByMaxLength(maxLength) {
      const ml = Number(maxLength) || 20
      return `${Math.min(ml * 1.6, 90)}ch`
    },

    abrirHelperCampo(texto) {
      if (texto && texto.trim().length > 0) {
        Swal.fire({ icon: 'info', html: texto, confirmButtonText: 'OK', width: 500 })
      }
    },

    abrirDropdownEspecialidade() {
      this.dropdownEspecialidadeAberto = true
      this.filtrarEspecialidades()
    },

    onEspecialidadeSearchInput(e) {
      this.especialidadeSearch = e.target.value
      this.filtrarEspecialidades()
    },

    filtrarEspecialidades() {
      const termo = (this.especialidadeSearch || '').toLowerCase()
      if (!termo) {
        this.especialidadeFiltrada = this.tracado.especialidadeOptions
      } else {
        this.especialidadeFiltrada = this.tracado.especialidadeOptions.filter(op =>
          op.label.toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '').includes(
            termo.normalize('NFD').replace(/[̀-ͯ]/g, '')
          )
        )
      }
      this.dropdownEspecialidadeAberto = true
    },

    selecionarEspecialidade(op) {
      this.tracado.especialidade = op.value
      this.especialidadeSearch = op.label
      this.dropdownEspecialidadeAberto = false
      this.isRequiredPatientData = op.requiredPatientData ?? true
    },

    fecharDropdownEspecialidade(e) {
      const input = this.$el && this.$el.querySelector('#xpEspecialidade')
      const dropdown = this.$el && this.$el.querySelector('.dropdown-list')
      if (e && input && dropdown && !input.contains(e.target) && !dropdown.contains(e.target)) {
        this.dropdownEspecialidadeAberto = false
      }
    },

    async buscarListaDeArquivos() {
      const dicomPlusID = this.exam?.dicomPlusID
      const res = await this.getFileList(dicomPlusID)
      this.savedFiles = res
      this.attachedFilesCount = this.savedFiles.length
    },

    buscarAnexoTipo() {
      this.anexoTipoOptions = this.getFileTypes()
    },

    async abrirModalAnexarArquivos() {
      await this.buscarListaDeArquivos()
      this.showAnexarArquivos = true
    },

    fecharModalAnexarArquivos() {
      this.showAnexarArquivos = false
    },

    existePedidoMedicoVinculado(arquivosEmLote = []) {
      const pedidoMedico = CONSTANTS.DICOM_PLUS_TIPO_ANEXO.PEDIDO_MEDICO
      return [...(this.savedFiles || []), ...(arquivosEmLote || [])].some(
        (file) => Number(file.tipoAnexo) === pedidoMedico
      )
    },
    pedidoMedicoObrigatorioPendente(arquivosEmLote = []) {
      if (!this.exam?.exigePedidoMedico) return false
      if (this.existePedidoMedicoVinculado(arquivosEmLote)) return false
      showDialog({
        icon: 'warning',
        title: this.$t('medical_order_required_title'),
        text: this.$t('medical_order_required'),
        confirmButtonText: 'OK'
      })
      return true
    },
    async salvarArquivos({ files }) {
      if (this.pedidoMedicoObrigatorioPendente(files)) {
        return
      }
      const dicomPlusID = this.exam.dicomPlusID
      const filesPayload = []
      try {
        this.$root.$emit('showLoading')

        const validacao = await this.validarArquivosXPlus(
          files.map(file => ({ FileName: file.name, MD5: file.md5 }))
        )
        if (!validacao.success) {
          const mensagens = (validacao.errors || []).map(err =>
            this.$t('x_plus.dialog_message.file_already_in_database', { fileName: err.Property ?? err.property })
          )
          showDialog({
            icon: 'warning',
            title: this.$t('x_plus.dialog_message.warn'),
            text: mensagens.length ? mensagens.join('\n') : this.$t('x_plus.create_exam_fail'),
            confirmButtonText: 'OK'
          })
          return
        }

        for (const file of files) {
          const urlInfo = await this.criarUrlUpload(file)
          if (!urlInfo.success) {
            showDialog({ icon: 'error', title: this.$t('x_plus.dialog_message.fail'), text: urlInfo.message, confirmButtonText: 'OK' })
            continue
          }
          const uploadResult = await this.uploadStorage(urlInfo.uploadUrl, file.file)
          if (!uploadResult.success) {
            showDialog({ icon: 'error', title: this.$t('x_plus.dialog_message.fail_save_storage'), text: uploadResult.message, confirmButtonText: 'OK' })
            continue
          }
          filesPayload.push({ linkStorage: urlInfo.publicUrl, tipoAnexo: file.tipoAnexo })
        }
        if (filesPayload.length) {
          const resultado = await this.salvarAnexo({ dicomPlusID, files: filesPayload })
          if (!(resultado && resultado.success)) {
            showDialog({ icon: 'error', title: this.$t('x_plus.dialog_message.fail'), text: resultado.message || 'Erro ao salvar arquivos', confirmButtonText: 'OK' })
          } else {
            showDialog({ icon: 'success', title: this.$t('x_plus.dialog_message.success'), text: this.$t('x_plus.dialog_message.files_saved_succesfully'), confirmButtonText: 'OK' })
          }
        }
      } catch (e) {
        showDialog({ icon: 'error', title: this.$t('x_plus.dialog_message.fail_attach_file'), text: e, confirmButtonText: 'OK' })
      } finally {
        await this.buscarListaDeArquivos()
        this.$refs.importFilesModal && this.$refs.importFilesModal.clearImportedFiles && this.$refs.importFilesModal.clearImportedFiles()
        this.$root.$emit('hideLoading')
      }
    },

    async handleGetExamXView() {
      try {
        this.$root.$emit('showLoading')
        const files = await this.getFileList(this.exam.dicomPlusID)
        if (!files.length) {
          showDialog({ icon: 'warning', title: this.$t('x_plus.dialog_message.fail'), text: this.$t('x_plus.dialog_message.download_error_link'), confirmButtonText: 'OK' })
          return
        }
        const sorted = [...files].sort((a, b) => a.dicomPlusFileID - b.dicomPlusFileID)
        const result = await this.criarUrlAssinadaDownloadCloud(sorted[0].dicomPlusFileID)
        if (result?.url) {
          window.open(result.url, '_blank')
        }
      } catch (error) {
        showDialog({ icon: 'error', title: this.$t('x_plus.dialog_message.download_error'), text: error, confirmButtonText: 'OK' })
      } finally {
        this.$root.$emit('hideLoading')
      }
    },

    async handleDownloadLink(dicomPlusFileID) {
      try {
        this.$root.$emit('showLoading')
        const result = await this.criarUrlAssinadaDownloadCloud(dicomPlusFileID)
        if (result?.url) {
          window.open(result.url, '_blank')
        }
      } catch (error) {
        showDialog({ icon: 'error', title: this.$t('x_plus.dialog_message.download_error'), text: error, confirmButtonText: 'OK' })
      } finally {
        this.$root.$emit('hideLoading')
      }
    },

    async confirmaExcluirArquivoSalvo(id) {
      await showDialog({
        title: this.$t('x_plus.dialog_message.confirmation'),
        text: this.$t('x_plus.dialog_message.confirm_attachment_deletion'),
        confirmButtonText: this.$t('x_plus.yes'),
        denyButtonText: this.$t('x_plus.no'),
        icon: 'question',
        focusConfirm: false,
        focusDeny: true,
        confirmed: () => this.excluirArquivoSalvo(id)
      })
    },

    async excluirArquivoSalvo(id) {
      try {
        this.$root.$emit('showLoading')
        await this.deleteFile(id)
        await this.buscarListaDeArquivos()
        showDialog({ icon: 'success', title: this.$t('x_plus.dialog_message.success'), text: this.$t('x_plus.dialog_message.files_removed_succesfully'), confirmButtonText: 'OK' })
      } catch (error) {
        showDialog({ icon: 'error', title: this.$t('x_plus.dialog_message.error_validating_data'), text: error?.errors || error?.message || error, confirmButtonText: 'OK' })
      } finally {
        this.$root.$emit('hideLoading')
      }
    },

    onRequisicaoInput(e) {
      const onlyDigits = String(e.target.value || '').replace(/\D/g, '')
      if (e.target.value !== onlyDigits) e.target.value = onlyDigits
      this.requisicaoID = onlyDigits
    },

    validarEspecialidadeInput() {
      const termo = (this.especialidadeSearch || '').trim().toLowerCase()
      const encontrada = this.tracado.especialidadeOptions.find(op => op.label.toLowerCase() === termo)
      if (!encontrada) {
        this.especialidadeSearch = ''
        this.tracado.especialidade = ''
      } else {
        this.tracado.especialidade = encontrada.value
        this.especialidadeSearch = encontrada.label
      }
      this.dropdownEspecialidadeAberto = false
    }
  },

  computed: {
    isExamEditable() {
      return this.exam?.status === CONSTANTS.DICOM_PLUS_STATUS.PENDENTE
    },
    isPatientEditable() {
      return this.exam?.status === CONSTANTS.DICOM_PLUS_STATUS.PENDENTE
    },
    isGestorSaudeDisabled() {
      if (this.exam?.enviaParaGSaudeApp) {
        return !this.isExamNotExistGestorSaude
      }
      return false
    },
    cpfCampo() {
      return (this.camposComplementares || []).find(c => Number(c.special) === CONSTANTS.DICOM_PLUS_FONTE_CABECALHO_TAG.CPF) || null
    },
    isBuscarDisabled() {
      const id = String(this.requisicaoID || '').trim()
      return this.isExamNotExistGestorSaude === true || !this.isExamEditable || id === ''
    }
  },

  async mounted() {
    this.buscarPrioridades()
    await this.buscarCamposComplementares()
    await this.buscarPaciente()
    await this.carregarEspecialidades()
    await this.buscarAnexoTipo()
    this.storeInitialState()
    document.addEventListener('click', this.fecharDropdownEspecialidade)
  },

  beforeDestroy() {
    document.removeEventListener('click', this.fecharDropdownEspecialidade)
  }
}
